import { useQueries } from '@tanstack/react-query';
import { LifecycleNodeKind, useLinkedNodes } from '../../../components/LinkedChips/useLinkedNodes';
import { projectDataClient } from '../../../lib/connect/clients';
import { proposalHref } from '../../../lib/proposalHref';
import { useAttention } from '../../Library/attentionHooks';
import { useGoNoGoRecords } from '../goNoGoHooks';
import { useCaptureReviews } from '../reviewHooks';
// Consume the owning caches: writes in the existing panels invalidate these
// same keys. Errors never expose cached rows from a now-denied read.
export function useOverviewSources(captureProjectId, zouProjectId) {
    const records = useGoNoGoRecords(captureProjectId);
    const reviews = useCaptureReviews(captureProjectId);
    const shared = useAttention();
    const linked = useLinkedNodes(LifecycleNodeKind.PURSUIT, captureProjectId);
    const events = shared.isSuccess && !shared.isError ? shared.data.items : [];
    const sourceIds = [
        ...new Set(events.filter((item)=>item.sourceType === 'govcon.library_items').map((item)=>item.sourceId))
    ];
    const library = useQueries({
        queries: sourceIds.map((id)=>({
                queryKey: [
                    'pursuit-overview-library-source',
                    id
                ],
                queryFn: ()=>projectDataClient.getLibraryItem({
                        id
                    }),
                retry: false
            }))
    });
    const sourceHrefs = new Map();
    for (const query of library){
        if (!query.isSuccess || query.isError) continue;
        const item = query.data.item;
        if (!item || item.owningScopeObjectType !== 'delivery.programs' || item.owningScopeObjectId !== zouProjectId) continue;
        sourceHrefs.set(item.id, `/library?record=${encodeURIComponent(item.id)}`);
    }
    const links = [];
    if (linked.isSuccess && !linked.isError) {
        for (const edge of linked.data.edges){
            const node = edge.node;
            if (!node) continue;
            const id = encodeURIComponent(node.zouProjectId || node.id);
            // Only workspace kinds with real destinations and independent server
            // authorization. Never use the resolver's unfiltered aggregate counts.
            const href = node.kind === LifecycleNodeKind.PROPOSAL ? proposalHref(node.zouProjectId || node.id) : node.kind === LifecycleNodeKind.RFI_RESPONSE ? `/rfi-response/${id}` : undefined;
            if (href) links.push({
                key: `${node.kind}:${node.id}`,
                title: `${node.kind === LifecycleNodeKind.PROPOSAL ? 'Proposal' : 'RFI response'}: ${node.label || 'Untitled'}`,
                href
            });
        }
    }
    const queries = [
        records,
        reviews,
        shared,
        linked,
        ...library
    ];
    return {
        records: records.isSuccess && !records.isError ? records.data : [],
        reviews: reviews.isSuccess && !reviews.isError ? reviews.data : [],
        shared: events.filter((event)=>event.sourceType === 'govcon.capture_tasks' || sourceHrefs.has(event.sourceId)).map((event)=>({
                ...event,
                href: sourceHrefs.get(event.sourceId)
            })),
        links,
        isLoading: queries.some((query)=>query.isLoading),
        failed: [
            records.isError && 'Scoped decisions',
            reviews.isError && 'Capture reviews',
            shared.isError && 'Shared source attention',
            (linked.isError || library.some((query)=>query.isError)) && 'Linked records / sources'
        ].filter(Boolean).join(', '),
        refetch: ()=>Promise.all(queries.map((query)=>query.refetch()))
    };
}
